import React from 'react';

import { useClassName, useWindowSize } from 'common/hooks';

import Link from '../Link';
import useCustomStyle from '../../hooks';
import { getStylesByPrefix, parseVideoSettings } from '../../utils';

function getResponsiveAsset(asset, viewport) {
  if (!asset?.fields) return null;

  if (viewport.isMobile && asset.fields.mobile) return asset.fields.mobile;
  if (viewport.isTablet && asset.fields.tablet) return asset.fields.tablet;
  if (viewport.isDesktop && asset.fields.desktop) return asset.fields.desktop;

  return asset.fields.desktop || asset.fields.tablet || asset.fields.mobile;
}

export default function CardTypeV({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeV');
  const [setRef, styles] = useCustomStyle(item.fields.settings);
  const [, assetStyles] = useCustomStyle(item.fields.asset?.fields?.settings);
  const viewport = useWindowSize();
  const link = item.fields.links?.[0];
  const legacyLinkHref = styles['--link-href'];
  const containerStyles = getStylesByPrefix('--container-', styles);
  const titleStyles = getStylesByPrefix('--title-', styles);
  const titleHoverStyles = getStylesByPrefix('--title-hover-', styles);
  const subtitleStyles = getStylesByPrefix('--subtitle-', styles);
  const subtitleHoverStyles = getStylesByPrefix('--subtitle-hover-', styles);
  const customProperties = Object.fromEntries(
    Object.entries(styles).filter(([key]) => key.startsWith('--'))
  );
  const videoSettings = {
    ...parseVideoSettings(assetStyles),
    ...parseVideoSettings(styles),
  };
  const {
    autoplay: videoAutoplay = false,
    loop: videoLoop = true,
    muted: videoMuted = true,
    controls: videoControls = false,
    playsInline: videoPlaysInline = true,
    preload: videoPreload = 'metadata',
  } = videoSettings;

  const videoRef = React.useRef(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const responsiveAsset = getResponsiveAsset(item.fields.asset, viewport);
  const contentType = responsiveAsset?.fields?.file?.contentType || '';
  const isVideo = contentType.startsWith('video');
  const isImage = contentType.startsWith('image');
  const assetUrl = responsiveAsset?.fields?.file?.url;
  const assetAlt =
    responsiveAsset?.fields?.description ||
    responsiveAsset?.fields?.title ||
    item.fields.title ||
    '';

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (!isVideo || videoAutoplay) return;
    const video = videoRef.current;
    if (video) {
      video.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (!isVideo || videoAutoplay) return;
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  const settingsList = item?.fields?.settings;
  const types = (
    settingsList
      ? settingsList.filter((setting) => setting.fields.key === 'type')
      : []
  )
    .map((type) => type.fields.value)
    .join(' | ');
  const subtitle = types || item?.fields?.subtitle;

  const content = (
    <>
      {isImage && assetUrl && (
        <img src={assetUrl} alt={assetAlt} loading="lazy" decoding="async" />
      )}
      {isVideo && assetUrl && (
        <video
          ref={videoRef}
          src={assetUrl}
          autoPlay={videoAutoplay}
          loop={videoLoop}
          muted={videoMuted}
          controls={videoControls}
          playsInline={videoPlaysInline}
          preload={videoPreload}
        />
      )}
      {Boolean(
        item?.fields?.subtitle ||
          item?.fields?.title ||
          item?.fields?.links?.length
      ) && (
        <div
          {...(styles?.['--orientation'] === 'horizontal' && {
            className: classNames('horizontal'),
          })}>
          {subtitle && (
            <span
              style={{
                ...subtitleStyles,
                ...(isHovered ? subtitleHoverStyles : {}),
              }}>
              {subtitle}
            </span>
          )}
          {Boolean(item?.fields?.title) && (
            <h4
              style={{
                ...titleStyles,
                ...(isHovered ? titleHoverStyles : {}),
              }}>
              {item.fields.title}
            </h4>
          )}
        </div>
      )}
    </>
  );

  const sharedProps = {
    className: classNames('container'),
    style: {
      ...customProperties,
      ...containerStyles,
    },
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };

  if (link) {
    return (
      <Link
        {...sharedProps}
        component={link}
        refs={refs}
        handleTrack={handleTrack}>
        {content}
      </Link>
    );
  }

  const Container = legacyLinkHref ? 'a' : 'div';
  const containerProps = legacyLinkHref
    ? { href: legacyLinkHref, onClick: (event) => event.stopPropagation() }
    : {};

  return (
    <Container ref={setRef} {...sharedProps} {...containerProps}>
      {content}
    </Container>
  );
}

export { getResponsiveAsset };
